import { z } from 'zod'

// DigiCS, the website chat's bot (#284 B4): its prepared answers and settings as the CMS edits them. The landing
// Worker reads both from /api/public/chat-bank (cached 60 s), so a change reaches the chat within a minute.
// Only a CMS whose API has the CHAT binding (digimasi.com's) has these screens, like the live chat inbox.

export const DIGICS_LIMITS = {
  /** An answer's text, per language, in characters. */
  text: 600,
  /** The line the model reads to choose an answer. */
  whenItFits: 300,
  /** Emoji in one answer (none in a hand-over). */
  emoji: 1,
  /** Answers in the bank. */
  answers: 100,
} as const

/** Every kind the bank has. New answers are `answer` or `handover`; the seeded greeting/offtopic/bot_identity stay. */
export const CHAT_ANSWER_KINDS = ['answer', 'handover', 'greeting', 'offtopic', 'bot_identity'] as const
export const NEW_CHAT_ANSWER_KINDS = ['answer', 'handover'] as const
/** The two hand-over answers the flow always sends: they can be edited but never switched off. */
export const PROTECTED_CHAT_ANSWERS = ['handover', 'handover_closed'] as const
export const isProtectedAnswer = (code: string) => (PROTECTED_CHAT_ANSWERS as readonly string[]).includes(code)

/** Lowercase letters, digits and `_`, 2–40 long, starting with a letter (the landing Worker skips any other code). */
export const CHAT_ANSWER_CODE = /^[a-z][a-z0-9_]{1,39}$/

export type ChatCopyProblem =
  | { rule: 'empty' }
  | { rule: 'too_long'; max: number }
  | { rule: 'banned'; word: string }
  | { rule: 'price' }
  | { rule: 'emoji'; max: number }

/**
 * Words the house copy rules never allow in a chat answer: nothing is "built with AI", no "source code", nothing is
 * "unlimited" (every limit is a number), no partner names, no formal Indonesian, and no WhatsApp (the chat hands over
 * in the chat and by email only).
 */
export const CHAT_BANNED_WORDS = ['built with ai', 'dibuat dengan ai', 'source code', 'kode sumber', 'unlimited', 'tanpa batas', 'trustwork', 'dengan hormat', 'adapun', 'whatsapp'] as const

const EMOJI = /\p{Extended_Pictographic}/gu

/** Characters as a person counts them (an emoji is one). */
const chars = (s: string) => [...s].length

/**
 * What breaks the house copy rules in one answer text, or [] when it is fine. Used by the API before it saves, by the
 * CMS while typing, and by the chat-bank test over the whole seed. Prices never go in an answer: they live on the
 * packages page, which the answer links to.
 */
export function chatCopyProblems(text: string, kind: string): ChatCopyProblem[] {
  const t = text.trim()
  if (!t) return [{ rule: 'empty' }]
  const out: ChatCopyProblem[] = []
  if (chars(t) > DIGICS_LIMITS.text) out.push({ rule: 'too_long', max: DIGICS_LIMITS.text })
  const lower = t.toLowerCase().replace(/\s+/g, ' ')
  for (const word of CHAT_BANNED_WORDS) if (lower.includes(word)) out.push({ rule: 'banned', word })
  if (/\bRp\.?\s?\d|\bIDR\s?\d|\d+\s?(rb|ribu|jt|juta)\b/i.test(t)) out.push({ rule: 'price' })
  const max = kind === 'handover' ? 0 : DIGICS_LIMITS.emoji
  if ((t.match(EMOJI) ?? []).length > max) out.push({ rule: 'emoji', max })
  return out
}

/** The problem as one English sentence (the API's error message; the CMS shows its own translation). */
export function chatCopyMessage(p: ChatCopyProblem): string {
  switch (p.rule) {
    case 'empty':
      return 'The text is empty'
    case 'too_long':
      return `The text is longer than ${p.max} characters`
    case 'banned':
      return `"${p.word}" is not allowed in a chat answer`
    case 'price':
      return 'No prices in a chat answer: link to the packages page instead'
    case 'emoji':
      return p.max === 0 ? 'No emoji in a hand-over answer' : `At most ${p.max} emoji in one answer`
  }
}

export const ChatAnswerSchema = z.object({
  code: z.string(),
  kind: z.enum(CHAT_ANSWER_KINDS),
  whenItFits: z.string(),
  textId: z.string(),
  textEn: z.string(),
  active: z.boolean(),
  sortOrder: z.number().int(),
  /** `handover` / `handover_closed`: editable, never switched off. */
  protected: z.boolean(),
  updatedAt: z.string(),
  /** Who saved it last (empty for the seed). */
  updatedBy: z.string(),
})
export type ChatAnswer = z.infer<typeof ChatAnswerSchema>

export const ChatAnswerListSchema = z.object({ items: z.array(ChatAnswerSchema) })
export type ChatAnswerList = z.infer<typeof ChatAnswerListSchema>

const answerText = z.string().trim().min(1, 'Write the text first')
const whenItFits = z
  .string()
  .trim()
  .min(10, 'Say in a sentence when this answer fits')
  .refine((v) => chars(v) <= DIGICS_LIMITS.whenItFits, `At most ${DIGICS_LIMITS.whenItFits} characters`)

export const UpdateChatAnswerRequestSchema = z.object({
  textId: answerText.optional(),
  textEn: answerText.optional(),
  whenItFits: whenItFits.optional(),
  active: z.boolean().optional(),
})
export type UpdateChatAnswerRequest = z.input<typeof UpdateChatAnswerRequestSchema>

export const CreateChatAnswerRequestSchema = z.object({
  code: z.string().trim().regex(CHAT_ANSWER_CODE, 'Use 2–40 lowercase letters, digits or _, starting with a letter'),
  kind: z.enum(NEW_CHAT_ANSWER_KINDS),
  textId: answerText,
  textEn: answerText,
  whenItFits,
  active: z.boolean().default(true),
})
export type CreateChatAnswerRequest = z.input<typeof CreateChatAnswerRequestSchema>

// ─── Settings ────────────────────────────────────────────────────────────────

export const DIGICS_MODES = ['off', 'suggest', 'auto'] as const
export const DIGICS_PROVIDERS = ['haiku', 'gemini'] as const
export type DigicsProvider = (typeof DIGICS_PROVIDERS)[number]
/** The secrets on the landing Worker the bot uses. The CMS only ever learns whether each one is set. */
export const DIGICS_SECRETS = ['ANTHROPIC_API_KEY', 'GEMINI_API_KEY', 'DIGICS_TELEGRAM_TOKEN'] as const
export type DigicsSecret = (typeof DIGICS_SECRETS)[number]

/** Bounds of every number setting (the landing Worker falls back to its default outside its own, wider bounds). */
export const DIGICS_BOUNDS = {
  minConfidence: { min: 0.3, max: 0.95 },
  monthlyCapIdr: { min: 0, max: 10_000_000 },
  costPerCallIdr: { min: 1, max: 10_000 },
  maxCallsPerChat: { min: 0, max: 200 },
} as const

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/
/** A model id as the chat accepts it (the landing Worker checks the same). */
export const DIGICS_MODEL_ID = /^[a-z0-9.-]{3,64}$/

/** What each listed model is like, shown next to it in the CMS (the CMS translates the note). */
export const DIGICS_MODEL_NOTES = ['cheapest', 'newest', 'smarter', 'older', 'smartest'] as const
export type DigicsModelNote = (typeof DIGICS_MODEL_NOTES)[number]
export type DigicsModelOption = { id: string; label: string; note: DigicsModelNote; recommended?: true }

/**
 * The models the CMS offers per provider (checked 27 Oct 2026). Google serves the 2.5 models only to accounts that
 * used them before, so new keys need a 3.x code. Any other id can still be typed in under "Other…".
 */
export const DIGICS_MODELS: Record<DigicsProvider, readonly DigicsModelOption[]> = {
  haiku: [
    { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', note: 'cheapest', recommended: true },
    { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', note: 'smarter' },
    { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', note: 'smartest' },
  ],
  gemini: [
    { id: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite', note: 'cheapest', recommended: true },
    { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', note: 'newest' },
    { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash', note: 'smarter' },
    { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite', note: 'older' },
  ],
}
/** A numeric chat id (groups are negative) or a public @channel name, as the landing Worker accepts it. */
export const TELEGRAM_CHAT_ID = /^-?\d{1,20}$|^@[A-Za-z0-9_]{5,32}$/

const int = (b: { min: number; max: number }, what: string) =>
  z
    .number()
    .int(`${what} must be a whole number`)
    .min(b.min, `${what} must be at least ${b.min.toLocaleString('en')}`)
    .max(b.max, `${what} must be at most ${b.max.toLocaleString('en')}`)

export const DigicsSettingsSchema = z
  .object({
    mode: z.enum(DIGICS_MODES),
    provider: z.enum(DIGICS_PROVIDERS),
    haikuModel: z.string().trim().regex(DIGICS_MODEL_ID, 'Model ids are 3–64 lowercase letters, digits, dots or dashes'),
    geminiModel: z.string().trim().regex(DIGICS_MODEL_ID, 'Model ids are 3–64 lowercase letters, digits, dots or dashes'),
    minConfidence: z
      .number()
      .min(DIGICS_BOUNDS.minConfidence.min, `Minimum confidence is between ${DIGICS_BOUNDS.minConfidence.min} and ${DIGICS_BOUNDS.minConfidence.max}`)
      .max(DIGICS_BOUNDS.minConfidence.max, `Minimum confidence is between ${DIGICS_BOUNDS.minConfidence.min} and ${DIGICS_BOUNDS.minConfidence.max}`),
    monthlyCapIdr: int(DIGICS_BOUNDS.monthlyCapIdr, 'The monthly cap'),
    costPerCallHaikuIdr: int(DIGICS_BOUNDS.costPerCallIdr, 'The cost per call'),
    costPerCallGeminiIdr: int(DIGICS_BOUNDS.costPerCallIdr, 'The cost per call'),
    maxCallsPerChat: int(DIGICS_BOUNDS.maxCallsPerChat, 'Calls per chat'),
    /** ISO weekdays, 1 = Monday … 7 = Sunday. */
    staffDays: z.array(z.number().int().min(1).max(7)).min(1, 'Pick at least one staff day').max(7),
    staffStart: z.string().regex(HHMM, 'Use HH:MM, e.g. 09:00'),
    staffEnd: z.string().regex(HHMM, 'Use HH:MM, e.g. 17:00'),
    telegramChatId: z.string().trim().refine((v) => v === '' || TELEGRAM_CHAT_ID.test(v), 'A Telegram chat id is a number (groups start with -) or @channelname'),
  })
  .refine((s) => s.staffStart < s.staffEnd, { message: 'Staff hours must end after they start', path: ['staffEnd'] })
export type DigicsSettings = z.infer<typeof DigicsSettingsSchema>

/** The staff hours' time zone: fixed, the chat runs on Jakarta time. */
export const DIGICS_TIMEZONE = 'Asia/Jakarta'

/** The settings as chat_settings rows (key → text), the way the landing Worker reads them. */
export function digicsSettingsToRows(s: DigicsSettings): Record<string, string> {
  return {
    mode: s.mode,
    provider: s.provider,
    haiku_model: s.haikuModel,
    gemini_model: s.geminiModel,
    min_confidence: String(s.minConfidence),
    monthly_cap_idr: String(s.monthlyCapIdr),
    cost_per_call_haiku_idr: String(s.costPerCallHaikuIdr),
    cost_per_call_gemini_idr: String(s.costPerCallGeminiIdr),
    max_calls_per_chat: String(s.maxCallsPerChat),
    staff_days: [...new Set(s.staffDays)].sort().join(','),
    staff_start: s.staffStart,
    staff_end: s.staffEnd,
    timezone: DIGICS_TIMEZONE,
    telegram_chat_id: s.telegramChatId,
  }
}

const DEFAULTS: DigicsSettings = {
  mode: 'suggest',
  provider: 'haiku',
  haikuModel: 'claude-haiku-5-5',
  geminiModel: 'gemini-3.5-flash-lite',
  minConfidence: 0.7,
  monthlyCapIdr: 300_000,
  costPerCallHaikuIdr: 60,
  costPerCallGeminiIdr: 15,
  maxCallsPerChat: 20,
  staffDays: [1, 2, 3, 4, 5],
  staffStart: '09:00',
  staffEnd: '17:00',
  telegramChatId: '',
}

/** chat_settings rows as the form's values; a missing or unreadable value shows the seeded default. */
export function digicsSettingsFromRows(rows: Record<string, string>): DigicsSettings {
  const n = (k: string, d: number) => (rows[k] !== undefined && rows[k] !== '' && Number.isFinite(Number(rows[k])) ? Number(rows[k]) : d)
  const pick = <T extends string>(v: string | undefined, all: readonly T[], d: T): T => (all as readonly string[]).includes(v ?? '') ? (v as T) : d
  const days = (rows['staff_days'] ?? '').split(',').map(Number).filter((d) => Number.isInteger(d) && d >= 1 && d <= 7)
  return {
    mode: pick(rows['mode'], DIGICS_MODES, DEFAULTS.mode),
    provider: pick(rows['provider'], DIGICS_PROVIDERS, DEFAULTS.provider),
    haikuModel: rows['haiku_model'] || DEFAULTS.haikuModel,
    geminiModel: rows['gemini_model'] || DEFAULTS.geminiModel,
    minConfidence: n('min_confidence', DEFAULTS.minConfidence),
    monthlyCapIdr: n('monthly_cap_idr', DEFAULTS.monthlyCapIdr),
    costPerCallHaikuIdr: n('cost_per_call_haiku_idr', DEFAULTS.costPerCallHaikuIdr),
    costPerCallGeminiIdr: n('cost_per_call_gemini_idr', DEFAULTS.costPerCallGeminiIdr),
    maxCallsPerChat: n('max_calls_per_chat', DEFAULTS.maxCallsPerChat),
    staffDays: days.length ? [...new Set(days)].sort() : DEFAULTS.staffDays,
    staffStart: HHMM.test(rows['staff_start'] ?? '') ? rows['staff_start']! : DEFAULTS.staffStart,
    staffEnd: HHMM.test(rows['staff_end'] ?? '') ? rows['staff_end']! : DEFAULTS.staffEnd,
    telegramChatId: rows['telegram_chat_id'] ?? '',
  }
}

export const DigicsUsageSchema = z.object({
  /** YYYY-MM in Jakarta time. */
  month: z.string(),
  providers: z.array(z.object({ provider: z.string(), calls: z.number().int(), costIdr: z.number().int() })),
  totalIdr: z.number().int(),
})
export type DigicsUsage = z.infer<typeof DigicsUsageSchema>

// ─── Keys entered in the CMS ─────────────────────────────────────────────────

/**
 * Where the chat takes a key from: `cms` (entered here, sealed in the CMS database; it wins), `cloudflare` (the landing
 * Worker's secret of the same name), `none`, or `unknown` (not in the CMS, and the landing Worker could not be asked).
 */
export const DIGICS_KEY_SOURCES = ['cms', 'cloudflare', 'none', 'unknown'] as const
export const DigicsKeyStatusSchema = z.object({
  /** Entered in this CMS. */
  configured: z.boolean(),
  /** The last four characters of the CMS key, '' when none. */
  last4: z.string(),
  source: z.enum(DIGICS_KEY_SOURCES),
})
export type DigicsKeyStatus = z.infer<typeof DigicsKeyStatusSchema>

/** PUT /digics/keys/<name>: enter (or replace) a key, or remove it. Checked with digicsKeyProblem. */
export const DigicsKeyPatchSchema = z.discriminatedUnion('action', [z.object({ action: z.literal('set'), value: z.string().max(1_000) }), z.object({ action: z.literal('clear') })])
export type DigicsKeyPatch = z.infer<typeof DigicsKeyPatchSchema>

/** What a key must look like. The messages never repeat the value. */
const KEY_FORMAT: Record<DigicsSecret, { re: RegExp; message: string }> = {
  ANTHROPIC_API_KEY: { re: /^sk-ant-[A-Za-z0-9_-]{20,500}$/, message: 'An Anthropic API key starts with sk-ant- (copy it from console.anthropic.com)' },
  // Google AI Studio now issues "AQ." keys (letters, digits, _ - and dots) besides the older "AIza…" ones.
  GEMINI_API_KEY: { re: /^[A-Za-z0-9_.-]{30,500}$/, message: 'A Gemini API key is at least 30 letters, digits, ".", - or _ (AIza… or AQ.…, copy it from Google AI Studio)' },
  DIGICS_TELEGRAM_TOKEN: { re: /^\d{5,15}:[A-Za-z0-9_-]{35,200}$/, message: 'A Telegram bot token looks like 123456789:AA… (copy it from @BotFather)' },
}

/** Why a pasted key is refused (already trimmed), or null when it looks right. */
export function digicsKeyProblem(name: DigicsSecret, value: string): string | null {
  if (value === '') return 'Paste the key first'
  return KEY_FORMAT[name].re.test(value) ? null : KEY_FORMAT[name].message
}

/** How long the provider's own error sentence may be, as "Test the AI key" shows it. */
export const DIGICS_ERROR_DETAIL_MAX = 200
const MASKED = '•••'

/**
 * A provider's error sentence made safe to show: anything that looks like a key or token masked (sk-ant-…, AIza…,
 * AQ.…, any run of 32+ key characters), one line, at most DIGICS_ERROR_DETAIL_MAX characters.
 */
export function maskProviderDetail(text: string): string {
  const one = text
    .replace(/sk-ant-[A-Za-z0-9_-]*/g, `sk-ant-${MASKED}`)
    .replace(/AIza[A-Za-z0-9_-]*/g, `AIza${MASKED}`)
    .replace(/\bAQ\.[A-Za-z0-9_.-]*/g, `AQ.${MASKED}`)
    .replace(/[A-Za-z0-9_\-+/=]{32,}/g, MASKED)
    .replace(/\s+/g, ' ')
    .trim()
  const chars = [...one]
  return chars.length > DIGICS_ERROR_DETAIL_MAX ? `${chars.slice(0, DIGICS_ERROR_DETAIL_MAX - 1).join('')}…` : one
}

/** "Test the AI key": one tiny classify request ("halo", with the real menu) to the chosen provider. */
export const DigicsTestKeyResultSchema = z.object({
  ok: z.boolean(),
  provider: z.enum(DIGICS_PROVIDERS),
  reason: z.enum(['', 'no_key', 'key_refused', 'model_not_found', 'network', 'month_cap', 'no_bank', 'failed']),
  /** The provider's own error message (masked, ≤ 200 characters), '' when it gave none. Never the request. */
  detail: z.string().max(DIGICS_ERROR_DETAIL_MAX).default(''),
  /** The provider's HTTP status, 0 when no answer came (or none was asked). */
  status: z.number().int().default(0),
  /** The model id that was tried, '' when no request was sent. */
  model: z.string().default(''),
})
export type DigicsTestKeyResult = z.infer<typeof DigicsTestKeyResultSchema>

/** "Find chat id": the chats that wrote to the bot lately (Telegram getUpdates), newest first. Never the token. */
export const DigicsTelegramChatsSchema = z.object({
  chats: z.array(z.object({ id: z.string(), type: z.string(), title: z.string() })),
  reason: z.enum(['', 'no_token', 'bad_token', 'webhook', 'network', 'failed']),
})
export type DigicsTelegramChats = z.infer<typeof DigicsTelegramChatsSchema>

/** The test buttons (test alert, test the AI key, find chat id): each at most this often per person per hour. */
export const DIGICS_TESTS_PER_HOUR = 10

export const DigicsSettingsResponseSchema = z.object({
  settings: DigicsSettingsSchema,
  /** When and by whom the settings were last saved (empty for the seed). */
  updatedAt: z.string(),
  updatedBy: z.string(),
  /** From the landing Worker; null when it could not be reached. */
  live: z
    .object({
      siteOn: z.boolean(),
      usage: DigicsUsageSchema,
      /** Whether each secret is set on the landing Worker. Never the value. */
      secrets: z.object({ ANTHROPIC_API_KEY: z.boolean(), GEMINI_API_KEY: z.boolean(), DIGICS_TELEGRAM_TOKEN: z.boolean() }),
    })
    .nullable(),
  /** Each key as the chat uses it: entered in this CMS (wins), else the Worker secret, else none. Never a value. */
  keys: z.object({ ANTHROPIC_API_KEY: DigicsKeyStatusSchema, GEMINI_API_KEY: DigicsKeyStatusSchema, DIGICS_TELEGRAM_TOKEN: DigicsKeyStatusSchema }),
})
export type DigicsSettingsResponse = z.infer<typeof DigicsSettingsResponseSchema>

export const DigicsTestAlertResultSchema = z.object({ sent: z.boolean(), reason: z.enum(['', 'no_token', 'no_chat_id', 'failed']) })
export type DigicsTestAlertResult = z.infer<typeof DigicsTestAlertResultSchema>

/** The test message's text (the alert itself has no visitor data at all). */
export const DIGICS_TEST_ALERT_TEXT = 'DigiCS test alert'
